import { LoadingButton } from "@mui/lab";
import styled from "styled-components";
import { theme } from "../../../Customization/Theme";
import { getIndustryButtonColors } from "../../../Utils/IndustryStyle";

interface AppButtonProps {
  variant?:
  | `primary`
  | `outline`
  | `grey`
  | `text`
  | `primaryNoDisabled`
  | `primaryoutlinebutton`
  | `primaryfilledbutton`;
  [otherProps: string]: any;
  children?: React.ReactNode;
}

const AppSmallButton: React.FC<AppButtonProps> = ({
  variant = "primary",
  children,
  ...otherProps
}) => {
  if (variant === "outline")
    return <OutlineButton {...otherProps}>{children}</OutlineButton>;

  if (variant === "primaryoutlinebutton")
    return (
      <PrimaryOutlineButton {...otherProps}>{children}</PrimaryOutlineButton>
    );

  if (variant === "primaryfilledbutton")
    return (
      <PrimaryFilledButton {...otherProps}>{children}</PrimaryFilledButton>
    );

  if (variant === "grey")
    return <GreyButton {...otherProps}>{children}</GreyButton>;

  if (variant === "text")
    return <TextButton {...otherProps}>{children}</TextButton>;

  if (variant === "primaryNoDisabled")
    return (
      <PrimaryNoDisableButton {...otherProps}>
        {children}
      </PrimaryNoDisableButton>
    );

  return <PrimaryButton {...otherProps}>{children}</PrimaryButton>;
};
const commonButtonStyles = `
  width: auto;
  max-width: 100%;
  min-width: 0;

  /* Allow multi-line text */
  white-space: normal !important;

  /* Prevent overflow */
  overflow: hidden;

  /* Fix MUI internal span */
  span {
    display: block;
    white-space: normal !important;
    word-break: break-word;
  }
`;

export const GreyButton = styled(LoadingButton)`
  && {
    ${commonButtonStyles}
    outline: none;
    text-transform: capitalize;
    text-align: center;

    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    font-family: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.family;
  }};

    border-radius: ${({ theme: t }: any) =>
    t?.industry_style?.enabled ? t?.industry_style?.buttonRadius : "6px"};
    border: 1px solid ${({ theme: t }: any) => {
    return getIndustryButtonColors(t)?.border ?? theme.palette.default.placeholder;
  }} !important;

    background-color: ${({ theme: t }: any) => {
    return getIndustryButtonColors(t)?.background ?? "transparent";
  }};
    color: ${({ theme: t }: any) => {
    return getIndustryButtonColors(t)?.color ?? theme.palette.default.darkGrey;
  }};

    a {
      color: ${theme.palette.default.darkGrey};
      width: 100%;
    }

    &:hover {
      background-color: ${theme.palette.default.placeholder};
      color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.color;
  }};

      a {
        color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.color;
  }};
        width: 100%;
      }
    }
  }
`;

export const TextButton = styled(LoadingButton)`
  && {
    ${commonButtonStyles}
    outline: none;
    text-transform: capitalize;
    text-align: center;

    padding: 2px 8px;
    font-size: 14px;
    font-weight: 500;
    font-family: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.family;
  }};

    border-radius: ${({ theme }) =>
    theme?.industry_style?.enabled ? theme?.industry_style?.buttonRadius : "6px"};
    border: none;

    background-color: transparent;
    color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.color ??
      theme?.components?.bot_options_standard?.normal?.background?.color
    );
  }};

    a {
      color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.text?.color;
  }};
      width: 100%;
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
      color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.background
      ?.color;
  }};

      a {
        color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.color;
  }};
        width: 100%;
      }
    }
  }
`;

export const OutlineButton = styled(LoadingButton)`
  && {
    ${commonButtonStyles}
    outline: none;
    text-transform: none;
    text-align: center;

    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    font-family: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.family;
  }};

    border-radius: ${({ theme }) =>
    theme?.industry_style?.enabled ? theme?.industry_style?.buttonRadius : "6px"};
    border: 1px solid
      ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.border ??
      theme?.components?.bot_options_standard?.hover?.text?.color
    );
  }} !important;

    background-color: ${({ theme }) => {
    return getIndustryButtonColors(theme)?.background ?? "transparent";
  }};
    color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.color ??
      theme?.components?.bot_options_standard?.hover?.text?.color
    );
  }};

    a {
      color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.color ??
      theme?.components?.bot_options_standard?.hover?.text?.color
    );
  }};
      width: 100%;
      font-family: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.family;
  }} !important;
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: Poppins !important;
      gap: 12px;
      svg {
        font-size: 17px;
      }
    }

    &:hover {
      background-color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.background
      ?.color;
  }};
      color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.color;
  }};

      a {
        color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.color;
  }};
        width: 100%;
      }
    }
    &:disabled {
      background-color: transparent;
      color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.text?.color;
  }};

      a {
        color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.text?.color;
  }};
        width: 100%;
      }
    }
  }
`;

export const PrimaryOutlineButton = styled(LoadingButton)`
  && {
    ${commonButtonStyles}
    outline: none;
    text-transform: none;
    text-align: left;

    padding: 6px 12px;
    font-size: 14px;
    font-weight: 400;
    // height: 30px;
    line-height: 17px;
    font-family: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.family;
  }};

    border-radius: ${({ theme }) =>
    theme?.industry_style?.enabled ? theme?.industry_style?.buttonRadius : "15px"};
    border: 1px solid
    ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.border ??
      theme?.components?.bot_options_standard?.normal?.background?.color
    );
  }} !important;

    background-color: ${({ theme }) => {
    return getIndustryButtonColors(theme)?.background ?? "transparent";
  }};
    color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.color ??
      theme?.components?.bot_options_standard?.normal?.background?.color
    );
  }};

    a {
      color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.color ??
      theme?.components?.bot_options_standard?.hover?.text?.color
    );
  }};
      width: 100%;
      font-family: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.family;
  }} !important;
      display: flex;
      justify-content: center;
      align-items: center;
      font-family: Poppins !important;
      gap: 12px;
      svg {
        font-size: 17px;
      }
    }

    &:hover {
      border: 1px solid
        ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.box
      ?.borderColor;
  }} !important;
      background-color: ${({ theme }) => {
    return (
      theme?.components?.bot_options_standard?.hover?.background?.color ??
      "transparent"
    );
  }};
      color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.text?.color;
  }};
      a {
        color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.text?.color;
  }};
        width: 100%;
      }
    }
    &:disabled {
      pointer-events: auto;
      cursor: not-allowed;

      background-color: ${({ theme }) => {
    return theme?.palette?.default?.lightGrey;
  }};
      color: ${({ theme }) => {
    return theme?.palette?.default?.darkGrey;
  }};
      border: 1px solid
        ${({ theme }) => {
    return theme?.palette?.default?.darkGrey;
  }} !important;
      a {
        color: ${({ theme }) => {
    return theme?.palette?.default?.darkGrey;
  }};
        width: 100%;
      }
      pointer-events: all;
      cursor: not-allowed;
    }
  }
`;

export const PrimaryButton = styled(LoadingButton)`
  && {
    ${commonButtonStyles}
    outline: none;
    text-transform: capitalize;
    text-align: center;
    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    font-family: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.family;
  }};

    border-radius: ${({ theme }) =>
    theme?.industry_style?.enabled ? theme?.industry_style?.buttonRadius : "6px"};
    border: 1px solid
      ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.border ??
      theme?.components?.bot_options_standard?.normal?.box?.borderColor
    );
  }} !important;

    background-color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.background ??
      theme?.components?.bot_options_standard?.normal?.background?.color
    );
  }};
    color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.color ??
      theme?.components?.bot_options_standard?.normal?.text?.color
    );
  }};

    a {
      display: flex;
      align-items: center;
      justify-content: center;
      text-decoration: none;
      color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.color ??
      theme?.components?.bot_options_standard?.normal?.text?.color
    );
  }};
      width: 100%;
      text-decoration: none;
      svg {
        margin-right: 10px;
        max-width: 13px;
      }
    }

    &:hover {
      border: 1px solid
        ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.box
      ?.borderColor;
  }} !important;
      background-color: ${({ theme }) => {
    return (
      theme?.components?.bot_options_standard?.hover?.background?.color ??
      "transparent"
    );
  }};
      color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.text?.color;
  }};
      a {
        color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.text?.color;
  }};
        width: 100%;
      }
    }

    &:disabled {
      pointer-events: auto;
      cursor: not-allowed;

      background-color: ${({ theme }) => {
    return theme?.palette?.default?.lightGrey;
  }};
      color: ${({ theme }) => {
    return theme?.palette?.default?.darkGrey;
  }};
      border: 1px solid
        ${({ theme }) => {
    return theme?.palette?.default?.darkGrey;
  }} !important;
      a {
        color: ${({ theme }) => {
    return theme?.palette?.default?.darkGrey;
  }};
        width: 100%;
      }
      pointer-events: all;
      cursor: not-allowed;
    }
  }
`;

export const PrimaryFilledButton = styled(LoadingButton)`
  && {
    ${commonButtonStyles}
    outline: none;
    text-transform: capitalize;
    text-align: left;
    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    line-height: 17px;
    // height: 30px;
    align-items: center;
    font-family: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.family;
  }};

    border-radius: ${({ theme }) =>
    theme?.industry_style?.enabled ? theme?.industry_style?.buttonRadius : "15px"};
    border: 1px solid
      ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.border ??
      theme?.components?.bot_options_standard?.normal?.box?.borderColor
    );
  }} !important;

    background-color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.background ??
      theme?.components?.bot_options_standard?.normal?.background?.color
    );
  }};
    color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.color ??
      theme?.components?.bot_options_standard?.normal?.text?.color
    );
  }};

    a {
      display: flex;
      align-items: center;
      justify-content: center;
      text-decoration: none;
      color: ${({ theme }) => {
    return (
      getIndustryButtonColors(theme)?.color ??
      theme?.components?.bot_options_standard?.normal?.text?.color
    );
  }};
      width: 100%;
      text-decoration: none;
      svg {
        margin-right: 10px;
        max-width: 13px;
      }
    }

    &:hover {
      border: 1px solid
        ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.box
      ?.borderColor;
  }} !important;
      background-color: ${({ theme }) => {
    return (
      theme?.components?.bot_options_standard?.hover?.background?.color ??
      "transparent"
    );
  }};
      color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.text?.color;
  }};
      a {
        color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.hover?.text?.color;
  }};
        width: 100%;
      }
    }

    &:disabled {
      pointer-events: auto;
      cursor: not-allowed;

      background-color: ${({ theme }) => {
    return theme?.palette?.default?.lightGrey;
  }};
      color: ${({ theme }) => {
    return theme?.palette?.default?.darkGrey;
  }};
      border: 1px solid
        ${({ theme }) => {
    return theme?.palette?.default?.darkGrey;
  }} !important;
      a {
        color: ${({ theme }) => {
    return theme?.palette?.default?.darkGrey;
  }};
        width: 100%;
      }
      pointer-events: all;
      cursor: not-allowed;
    }
  }
`;

export const PrimaryNoDisableButton = styled(PrimaryButton)`
  && {
    &:disabled {
      background-color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.background
      ?.color;
  }};
      color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.color;
  }};

      a {
        color: ${({ theme }) => {
    return theme?.components?.bot_options_standard?.normal?.text?.color;
  }};
        width: 100%;
      }
      pointer-events: all;
      cursor: not-allowed;
    }
  }
`;

export default AppSmallButton;
